import { useStore, selectDiscoveredTypes, selectLocale } from '@/infra/store';
import { allTypeCombinations, typeSignature, typeToHueOffset } from '@/domain/genetics/alchemy';
import { getTypeName } from '@/domain/genetics/type-names';
import { paletteFromHue } from '@/domain/genetics/palette';
import { useT } from '@/infra/i18n';
import { COLORS, FONT, SPACING, RADIUS } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from './modal-shared';
import { enemyDefById } from '@/domain/combat/enemy-defs';

const ASSET_ROOT = '/assets/generated/house';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ALL = allTypeCombinations();
const GROUPED = (() => {
  const m = new Map<number, typeof ALL>();
  for (const c of ALL) {
    if (!m.has(c.length)) m.set(c.length, []);
    m.get(c.length)!.push(c);
  }
  return m;
})();

function hexToCSS(h: number): string {
  return '#' + h.toString(16).padStart(6, '0');
}

export function BestiaryModal({ open, onClose }: Props) {
  const t = useT();
  const discovered = useStore(selectDiscoveredTypes);
  const locale = useStore(selectLocale);
  const world = useStore((s) => s.world);
  if (!open) return null;
  const set = new Set(discovered);

  return (
    <div role="dialog" data-testid="bestiary-modal" style={overlayStyle} onClick={onClose}>
      <div style={{ ...cardStyle, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={title}>
          <img src={`${ASSET_ROOT}/icon-bestiary.png`} alt="" draggable={false} style={titleIcon} />
          {t('bestiary.title')}
        </h2>
        <p style={progress}>{t('bestiary.discovered', { n: String(set.size) })}</p>
        {[1, 2, 3, 4, 5, 6].map((size) => {
          const combos = GROUPED.get(size);
          if (!combos) return null;
          return (
            <div key={size} style={{ marginBottom: SPACING.md }}>
              <h3 style={groupTitle}>
                {size} {size === 1 ? t('bestiary.group.singular') : t('bestiary.group.plural')}
              </h3>
              <div style={grid}>
                {combos.map((combo) => {
                  const sig = typeSignature(combo);
                  const found = set.has(sig);
                  const hue = typeToHueOffset(combo[0]!);
                  const pal = paletteFromHue(hue);
                  const bg = found ? hexToCSS(pal.base) : COLORS.surfaceAlt;
                  const color = found ? '#000' : COLORS.textFaint;
                  return (
                    <div key={sig} style={{ ...cell, background: bg, color }}>
                      <span style={{ fontSize: FONT.sizeXs, fontWeight: found ? 600 : 400 }}>
                        {found ? getTypeName(sig, locale) : '???'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        <section style={collectionSection}>
          <h3 style={groupTitle}>{t('bestiary.fauna.title')}</h3>
          <div style={collectionGrid}>
            {Object.entries(world.enemyKills ?? {})
              .filter(([, kills]) => kills > 0)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([id, kills]) => (
                <div key={id} style={collectionCard}>
                  <strong style={cardName}>{enemyDefById(id)?.id ?? id}</strong>
                  <span style={cardMeta}>{t('bestiary.enemies.kills', { n: String(kills) })}</span>
                </div>
              ))}
            {Object.keys(world.enemyKills ?? {}).length === 0 && <span style={cardMeta}>{t('bestiary.empty')}</span>}
          </div>
        </section>

        <section style={collectionSection}>
          <h3 style={groupTitle}>{t('bestiary.wild.title')}</h3>
          <div style={collectionGrid}>
            {Object.entries(world.wildBestiary ?? {}).sort(([a], [b]) => a.localeCompare(b)).map(([id, entry]) => {
              const genes = [...entry.bodies, ...entry.ears, ...entry.horns, ...entry.tails, ...entry.markings, ...entry.types];
              return (
                <article key={id} style={collectionCard}>
                  <div style={wildHeader}>
                    <strong style={cardName}>{id}</strong>
                    {entry.dominantSeen && <span style={dominantBadge}>{t('bestiary.wild.dominant')}</span>}
                  </div>
                  <span style={cardMeta}>{entry.biome} · {t('bestiary.wild.encounters', { n: String(entry.encounters) })}</span>
                  <div style={chipRow}>{genes.map((gene) => <span key={gene} style={chip}>{gene}</span>)}</div>
                </article>
              );
            })}
            {Object.keys(world.wildBestiary ?? {}).length === 0 && <span style={cardMeta}>{t('bestiary.empty')}</span>}
          </div>
        </section>
        <div style={{ textAlign: 'right', marginTop: SPACING.md }}>
          <button data-testid="bestiary-close" onClick={onClose} style={primaryBtn}>
            {t('bestiary.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

const title: React.CSSProperties = {
  margin: 0,
  color: COLORS.accent,
  fontSize: FONT.sizeXl,
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.xs,
};
const titleIcon: React.CSSProperties = {
  width: 36,
  height: 36,
  objectFit: 'contain',
  imageRendering: 'auto',
};
const progress: React.CSSProperties = {
  margin: `${SPACING.xs}px 0 ${SPACING.lg}px 0`,
  color: COLORS.textDim,
  fontSize: FONT.sizeSm,
};
const groupTitle: React.CSSProperties = {
  margin: `0 0 ${SPACING.sm}px 0`,
  color: COLORS.textDim,
  fontSize: FONT.sizeMd,
  borderBottom: `1px solid ${COLORS.border}`,
  paddingBottom: 4,
};
const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
  gap: SPACING.xs + 2,
};
const cell: React.CSSProperties = {
  borderRadius: RADIUS.sm,
  padding: 6,
  textAlign: 'center',
  minHeight: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const primaryBtn: React.CSSProperties = {
  background: COLORS.accent,
  color: COLORS.bg,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontWeight: 600,
};
const collectionSection: React.CSSProperties = { marginTop: SPACING.lg };
const collectionGrid: React.CSSProperties = { display: 'grid', gap: SPACING.sm };
const collectionCard: React.CSSProperties = {
  padding: SPACING.sm,
  border: `1px solid ${COLORS.border}`,
  background: COLORS.surfaceAlt,
  borderRadius: RADIUS.sm,
  display: 'grid',
  gap: 5,
};
const wildHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 };
const cardName: React.CSSProperties = { color: COLORS.text, fontFamily: FONT.mono, fontSize: FONT.sizeSm };
const cardMeta: React.CSSProperties = { color: COLORS.textDim, fontSize: FONT.sizeXs };
const dominantBadge: React.CSSProperties = {
  color: '#24180d', background: '#ffe08a', border: '1px solid #8c632f',
  padding: '2px 5px', borderRadius: 3, fontFamily: FONT.mono, fontSize: FONT.sizeXs,
};
const chipRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };
const chip: React.CSSProperties = {
  color: COLORS.textDim, background: COLORS.surfaceSoft, border: `1px solid ${COLORS.border}`,
  padding: '2px 5px', borderRadius: 3, fontFamily: FONT.mono, fontSize: FONT.sizeXs,
};
